// Daily Brief — full-screen morning takeover.
//
// Visual: same backdrop + particle field as the Login page (so the
// transition from login → brief feels continuous), with everything else
// stripped away. Just a title, then body sentences that wipe in
// left-to-right one at a time, then a thin rule, then a Continue
// button that fades in last.
//
// Mounted at the App-shell level (sibling to MusicInterstitial). On
// brief mount the parent fires Shao Kahn voice; on Continue click the
// parent enables music. The component itself is "dumb" — show/hide is
// the parent's call.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getDailyBrief, type DailyBrief } from "../api";
import { ParticleField } from "./ParticleField";

export const DAILY_BRIEF_SEEN_KEY = "njrc.dailyBriefSeen"; // value = "YYYY-MM-DD"

/** Today's NJ date string, mirroring server's todayInNJ(). */
export function todayInNJ(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** Has the user already seen today's brief in this browser? */
export function dailyBriefAlreadySeenToday(): boolean {
  try {
    return localStorage.getItem(DAILY_BRIEF_SEEN_KEY) === todayInNJ();
  } catch {
    return false;
  }
}

interface Props {
  onDismiss: () => void;
}

export function DailyBriefOverlay({ onDismiss }: Props): JSX.Element {
  const [brief, setBrief] = useState<DailyBrief | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    let alive = true;
    getDailyBrief()
      .then((b) => alive && setBrief(b))
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => { alive = false; };
  }, []);

  // Body scroll lock + Enter as a dismiss accelerator.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") dismiss();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    if (fading) return;
    setFading(true);
    setTimeout(() => onDismiss(), 600);
  };

  // Split body into sentences for the per-sentence wipe reveal. Splits
  // on period/question/exclamation followed by whitespace, keeping the
  // punctuation. Sentences ≤4 chars are merged into the previous one
  // (catches abbreviations like "St." that escape the regex).
  const sentences = splitIntoSentences(brief?.body ?? "");

  return (
    <div
      role="dialog"
      aria-live="polite"
      className="dark-tile"
      style={{
        // Standalone screen — not an overlay. BriefGate renders this
        // INSTEAD of AuthedShell, so we just need to fill the viewport
        // and own the visual canvas. No position:fixed, no z-index
        // jockeying, no peek-through possibility.
        position: "relative",
        minHeight: "100vh",
        opacity: fading ? 0 : 1,
        transition: "opacity 0.6s ease",
        background: "#0a0a14",
        overflow: "hidden",
      }}
    >
      {/* Police-siren gradient — same recipe as the Login page so the
          transition from login → brief feels seamless. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: [
            "radial-gradient(ellipse 60% 70% at 15% 95%, rgba(228, 0, 43, 0.55), transparent 65%)",
            "radial-gradient(ellipse 60% 70% at 85% 95%, rgba(0, 70, 255, 0.55), transparent 65%)",
            "radial-gradient(ellipse 55% 45% at 25% 25%, rgba(228, 0, 43, 0.25), transparent 70%)",
            "radial-gradient(ellipse 55% 45% at 75% 25%, rgba(0, 70, 255, 0.25), transparent 70%)",
            "radial-gradient(ellipse 70% 50% at 50% 45%, rgba(0,0,0,0.30), transparent 70%)",
            "radial-gradient(ellipse 110% 80% at 50% 50%, transparent 50%, rgba(0,0,0,0.50) 100%)",
            "linear-gradient(180deg, #050608 0%, #0b0c14 50%, #0a050a 100%)",
          ].join(", "),
        }}
      />

      {/* Same particle field as the login page — drifting embers. */}
      <ParticleField />

      {/* Center stage */}
      <div
        className="relative z-10 flex min-h-screen items-center justify-center px-6 py-10"
      >
        <div
          style={{
            width: "min(880px, 92vw)",
            textAlign: "center",
          }}
        >
          {brief ? (
            <BriefContent brief={brief} onContinue={dismiss} />
          ) : error ? (
            <div
              className="italic"
              style={{
                fontFamily: "var(--font-serif)",
                color: "rgba(255,255,255,0.7)",
                fontSize: 18,
              }}
            >
              {error}
            </div>
          ) : (
            <LoadingShim />
          )}
        </div>
      </div>

      <style>{`
        @keyframes brief-wipe-in {
          from { clip-path: inset(0 100% 0 0); opacity: 0; }
          to   { clip-path: inset(0 0%   0 0); opacity: 1; }
        }
        @keyframes brief-fade-up {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes brief-rule-grow {
          from { transform: scaleX(0); opacity: 0; }
          to   { transform: scaleX(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function BriefContent({
  brief,
  onContinue,
}: {
  brief: DailyBrief;
  onContinue: () => void;
}): JSX.Element {
  const sentences = splitIntoSentences(brief.body);
  // Slowed-down stagger so the wipe feels deliberate rather than rushed.
  // Title shows at 0.0s; sentences start at 1.1s with 1.05s between
  // sentences; rule + button delayed accordingly.
  const TITLE_DELAY = 0.0;
  const SENTENCE_START = 1.1;
  const SENTENCE_STEP = 1.05;
  const SENTENCE_DURATION = 1.6; // up from 0.85s
  const sentencesEnd =
    SENTENCE_START + Math.max(0, sentences.length - 1) * SENTENCE_STEP + SENTENCE_DURATION;
  const RULE_DELAY = sentencesEnd + 0.4;
  const BUTTON_DELAY = sentencesEnd + 1.1;

  // Dynamic scale-to-fit: measure the natural height of the content
  // block AFTER mount, compare against viewport height, and apply a
  // CSS transform: scale(<=1) if needed so the Continue button is
  // ALWAYS visible without scrolling — regardless of brief length,
  // viewport height, or browser-chrome quirks. Recomputes on resize.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    const measure = () => {
      const el = contentRef.current;
      if (!el) return;
      // Reset to 1 first so we measure the natural height (not a
      // pre-scaled height from a previous run).
      el.style.transform = "scale(1)";
      const naturalH = el.scrollHeight;
      const viewportH = window.innerHeight;
      const padding = 80; // breathing room top + bottom combined
      const available = viewportH - padding;
      const newScale = naturalH > available ? available / naturalH : 1;
      setScale(newScale);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [brief, sentences.length]);

  return (
    <div
      ref={contentRef}
      style={{
        transform: `scale(${scale})`,
        transformOrigin: "center top",
        transition: "transform 0.3s ease",
      }}
    >
      {/* Title — italic Fraunces, big. Fades up gently. */}
      <h1
        style={{
          fontFamily: "Fraunces, var(--font-serif), serif",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "clamp(40px, 6.5vw, 84px)",
          lineHeight: 1.05,
          letterSpacing: "-0.015em",
          color: "white",
          fontVariationSettings: '"opsz" 144, "SOFT" 60',
          opacity: 0,
          animation: `brief-fade-up 0.9s ease-out ${TITLE_DELAY}s both`,
          textShadow: "0 4px 30px rgba(0,0,0,0.6)",
          marginBottom: "clamp(28px, 4vw, 56px)",
        }}
      >
        {brief.title}
      </h1>

      {/* Body — one sentence per line, each wiping in left-to-right.
          Explicit Fraunces font-family so it's unambiguous regardless
          of how var(--font-serif) is configured. */}
      <div
        style={{
          fontFamily: "Fraunces, var(--font-serif), serif",
          fontStyle: "italic",
          fontSize: "clamp(20px, 2.4vw, 30px)",
          lineHeight: 1.55,
          color: "rgba(255,255,255,0.92)",
          maxWidth: "44ch",
          marginInline: "auto",
          fontVariationSettings: '"opsz" 36',
          textAlign: "left",
          fontWeight: 400,
        }}
      >
        {sentences.map((s, i) => (
          <p
            key={i}
            style={{
              margin: i === 0 ? 0 : "0.6em 0 0",
              opacity: 0,
              animation: `brief-wipe-in ${SENTENCE_DURATION}s cubic-bezier(0.2, 0.8, 0.2, 1) ${SENTENCE_START + i * SENTENCE_STEP}s both`,
            }}
          >
            {s}
          </p>
        ))}
      </div>

      {/* Thin rule — grows from center after the body finishes. */}
      <div
        aria-hidden="true"
        style={{
          marginTop: "clamp(36px, 5vw, 60px)",
          marginInline: "auto",
          width: "min(280px, 60vw)",
          height: 1,
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
          opacity: 0,
          transform: "scaleX(0)",
          animation: `brief-rule-grow 0.9s ease-out ${RULE_DELAY}s both`,
        }}
      />

      {/* Continue button — fades in last, after the rule. */}
      <button
        type="button"
        onClick={onContinue}
        style={{
          marginTop: "clamp(28px, 4vw, 44px)",
          background: "transparent",
          color: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 999,
          padding: "14px 32px",
          fontFamily: "var(--font-mono)",
          fontSize: 14,
          letterSpacing: "0.18em",
          fontWeight: 600,
          cursor: "pointer",
          opacity: 0,
          animation: `brief-fade-up 0.7s ease-out ${BUTTON_DELAY}s both`,
          transition: "background 0.2s ease, color 0.2s ease, border-color 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.08)";
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.7)";
          e.currentTarget.style.color = "white";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)";
          e.currentTarget.style.color = "rgba(255,255,255,0.92)";
        }}
      >
        CONTINUE
      </button>
    </div>
  );
}

function LoadingShim(): JSX.Element {
  const [dots, setDots] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d % 3) + 1), 450);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      style={{
        fontFamily: "var(--font-serif)",
        fontStyle: "italic",
        fontSize: "clamp(20px, 2.4vw, 32px)",
        color: "rgba(255,255,255,0.55)",
      }}
    >
      Reading the queue{".".repeat(dots)}
    </div>
  );
}

/** Split body into sentences. Splits on .!? followed by whitespace,
 *  keeping the punctuation. Sentences ≤4 chars are merged backward
 *  (catches abbreviations like "St." that aren't sentence boundaries). */
function splitIntoSentences(body: string): string[] {
  if (!body) return [];
  const raw = body.split(/(?<=[.!?])\s+/);
  const out: string[] = [];
  for (const piece of raw) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    if (trimmed.length <= 4 && out.length > 0) {
      out[out.length - 1] = `${out[out.length - 1]} ${trimmed}`;
    } else {
      out.push(trimmed);
    }
  }
  return out;
}
